"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";

const KNOWN_UNAVAILABLE_CMS_IMAGE_PATHS = new Set([
  "/uploads/Why_Nutrition_is_the_Key_to_Taking_Care_of_Yourself_61888362ad.png"
]);

function isKnownUnavailableCmsImage(candidate: string) {
  try {
    return KNOWN_UNAVAILABLE_CMS_IMAGE_PATHS.has(
      new URL(candidate, "https://ama.invalid").pathname
    );
  } catch {
    return false;
  }
}

type Props = {
  candidates: string[];
  alt: string;
  className?: string;
  fallbackClassName?: string;
  fallbackLabel: string;
  dataNodeId?: string;
  eager?: boolean;
  sizes?: string;
};

export default function RecordAwareImage({
  candidates,
  alt,
  className,
  fallbackClassName,
  fallbackLabel,
  dataNodeId,
  eager = false,
  sizes = "(max-width: 767px) 100vw, 650px"
}: Props) {
  // Filter permanent CMS failures during SSR so eager images never emit a URL
  // whose error event can fire before React hydrates the fallback handler.
  const usableCandidates = candidates.filter(
    (candidate) => candidate && !isKnownUnavailableCmsImage(candidate)
  );
  const [candidateIndex, setCandidateIndex] = useState(0);
  const imageRef = useRef<HTMLImageElement>(null);
  const handledFailureRef = useRef<string | null>(null);
  const candidatesKey = usableCandidates.join("|");

  useEffect(() => {
    handledFailureRef.current = null;
    setCandidateIndex(0);
  }, [candidatesKey]);

  const src = usableCandidates[candidateIndex];

  useEffect(() => {
    const image = imageRef.current;
    if (!src || !image?.complete || image.naturalWidth > 0 || handledFailureRef.current === src) {
      return;
    }

    handledFailureRef.current = src;
    setCandidateIndex((index) => index + 1);
  }, [src]);

  if (!src) {
    return (
      <div
        className={fallbackClassName || className}
        role="img"
        aria-label={alt}
        data-node-id={dataNodeId}
      >
        <span>{fallbackLabel}</span>
      </div>
    );
  }

  return (
    <Image
      ref={imageRef}
      className={className}
      src={src}
      alt={alt}
      fill
      priority={eager}
      quality={82}
      sizes={sizes}
      onError={() => {
        if (handledFailureRef.current === src) return;
        handledFailureRef.current = src;
        setCandidateIndex((index) => index + 1);
      }}
      data-node-id={dataNodeId}
    />
  );
}
